// ============================================================================
// 🚗 INTERFACES PRINCIPAIS DO VEHICLE
// ============================================================================

/**
 * Enum para tipos de veículo
 */
export enum VehicleType {
    CAR = 'CAR',
    MOTORCYCLE = 'MOTORCYCLE',
}

/**
 * Enum para status de veículo
 */
export enum VehicleStatus {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    MAINTENANCE = 'MAINTENANCE',
}

/**
 * Interface base para Vehicle
 */
export interface IVehicle {
    readonly id: string;
    readonly plate: string;
    readonly type: VehicleType;
    readonly model: string;
    readonly status: VehicleStatus;
    readonly isActive: boolean;
    readonly companyId: string;
    readonly initialKm: number;
    readonly currentKm: number;
    readonly finalKm?: number;
    readonly notes?: string;
    readonly createdAt: Date;
    readonly updatedAt: Date;
    readonly deletedAt?: Date;
}

/**
 * DTO para criação de Vehicle
 */
export interface ICreateVehicleDto {
    plate: string;
    type: VehicleType;
    model: string;
    status?: VehicleStatus;
    isActive?: boolean;
    companyId: string;
    initialKm: number;
    currentKm?: number;
    notes?: string;
}

/**
 * DTO para atualização de Vehicle
 */
export interface IUpdateVehicleDto extends Partial<ICreateVehicleDto> {
    finalKm?: number;
}

// ============================================================================
// 📡 INTERFACES DE RESPOSTA DA API
// ============================================================================

/**
 * Resposta da API para Vehicle
 */
export interface IVehicleResponse extends IVehicle {
    // Relacionamentos podem ser incluídos aqui se necessário
    // company?: ICompanyResponse;
    // vehicleChecklists?: IVehicleChecklistResponse[];
    // supplies?: ISupplyResponse[];
    // motorizedServices?: IMotorizedServiceResponse[];
}

/**
 * Resposta paginada para Vehicle
 */
export interface IVehiclePaginationResponse {
    data: IVehicleResponse[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
}

// ============================================================================
// 🔍 INTERFACES DE FILTROS E BUSCA
// ============================================================================

/**
 * Filtros para busca de Vehicle
 */
export interface IVehicleFilters {
    plate?: string;
    type?: VehicleType;
    model?: string;
    status?: VehicleStatus;
    isActive?: boolean;
    companyId?: string;
    minKm?: number;
    maxKm?: number;
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: 'plate' | 'model' | 'type' | 'status' | 'currentKm' | 'createdAt';
    sortOrder?: 'asc' | 'desc';
}

// ============================================================================
// 📊 INTERFACES DE RESUMO E ESTATÍSTICAS
// ============================================================================

/**
 * Resumo de Vehicle para listagens
 */
export interface IVehicleSummary {
    id: string;
    plate: string;
    type: VehicleType;
    model: string;
    status: VehicleStatus;
    isActive: boolean;
    currentKm: number;
    createdAt: Date;
}

/**
 * Estatísticas de Vehicle
 */
export interface IVehicleStats {
    total: number;
    active: number;
    inactive: number;
    inMaintenance: number;
    byType: {
        [key in VehicleType]: number;
    };
    byStatus: {
        [key in VehicleStatus]: number;
    };
    averageKm: number;
    totalKm: number;
}

// ============================================================================
// 🎯 TIPOS UNION PARA FLEXIBILIDADE
// ============================================================================

/**
 * Tipo union para Vehicle (criação ou atualização)
 */
export type VehicleDto = ICreateVehicleDto | IUpdateVehicleDto;
